/* eslint-disable complexity */
import {Icon, TextSemiBold, Tooltip} from '@hm/fundamental'
import {mdiClose} from '@mdi/js'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {COLOR_PICKER_WIDGET_NAMESPACE} from '../consts'
import type {HsvColor} from '../types'
import {hexToHsv, hsvToHex, calculatePopoverPosition} from '../utils'

import {ColorButton} from './ColorButton'
import {ColorPreview} from './ColorPreview'
import {ResetLocalColorCell} from './ResetLocalColorCell'

interface ColorSelectorProps {
  defaultColor: string
  customColor?: string
  isDisabled?: boolean
  showResetButton?: boolean
  showDefaultColorAsFallback?: boolean
  showSelectedOutline?: boolean
  showDefaultColor?: boolean
  fullWidth?: boolean
  showTitles?: boolean
  onColorChange?: (color: string) => void | Promise<void>
  onColorReset?: () => void
  dataCy?: string
}

export const ColorSelector: React.FC<ColorSelectorProps> = (props) => {
  const {
    defaultColor,
    customColor,
    isDisabled = false,
    showResetButton = true,
    showDefaultColorAsFallback = false,
    showSelectedOutline = true,
    showDefaultColor = false,
    fullWidth = false,
    showTitles = false,
    onColorChange,
    onColorReset,
    dataCy = 'color-selector'
  } = props
  const {t} = useTranslation(COLOR_PICKER_WIDGET_NAMESPACE)
  const popoverRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const saturationRef = useRef<HTMLDivElement>(null)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [hsv, setHsv] = useState<HsvColor>({h: 0, s: 100, v: 100})
  const [isDragging, setIsDragging] = useState(false)
  const [colorFormat] = useState<'HEX'>('HEX')
  const pendingColorRef = useRef<string | null>(null)
  const hsvRef = useRef<HsvColor>(hsv)
  const [popoverPosition, setPopoverPosition] = useState<{
    top?: string
    bottom?: string
    left?: string
    right?: string
  }>({
    top: '100%',
    left: '0'
  })

  const [optimisticColor, setOptimisticColor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    hsvRef.current = hsv
  }, [hsv])

  useEffect(() => {
    if (customColor !== undefined) {
      setOptimisticColor(null)
    }
  }, [customColor])

  const currentHexColor = useMemo(() => hsvToHex(hsv.h, hsv.s, hsv.v), [hsv])

  const saveColor = useCallback(async () => {
    if (pendingColorRef.current && onColorChange) {
      const colorToSave = pendingColorRef.current
      const previousColor = customColor
      setOptimisticColor(colorToSave)
      setError(null)
      pendingColorRef.current = null

      try {
        await onColorChange(colorToSave)
      } catch (err) {
        setOptimisticColor(previousColor || null)
        setError(t('localColors.saveError'))
      }
    }
  }, [onColorChange, customColor, t])

  const handleColorChange = useCallback((color: string) => {
    pendingColorRef.current = color
  }, [])

  const handleOpenPicker = useCallback(() => {
    if (!isDisabled && onColorChange) {
      const effectiveColor = customColor || defaultColor
      setHsv(hexToHsv(effectiveColor))
      setPopoverPosition(calculatePopoverPosition(containerRef))
      setIsPickerOpen(true)
    }
  }, [isDisabled, onColorChange, customColor, defaultColor])

  const handleClosePicker = useCallback(async () => {
    await saveColor()
    setIsPickerOpen(false)
  }, [saveColor])

  const handleSaturationMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true)
      const rect = saturationRef.current?.getBoundingClientRect()
      if (rect) {
        const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
        const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height))
        const newS = (x / rect.width) * 100
        const newV = 100 - (y / rect.height) * 100
        // Use ref to get current hue value
        const newHsv = {h: hsvRef.current.h, s: newS, v: newV}
        setHsv(newHsv)
        handleColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v))
      }
    },
    [handleColorChange]
  )

  const handleHueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newH = parseFloat(event.target.value)
      const newHsv = {h: newH, s: hsvRef.current.s, v: hsvRef.current.v}
      setHsv(newHsv)
      handleColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v))
    },
    [handleColorChange]
  )

  const handleHexInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value.toUpperCase()
      if (value && !value.startsWith('#')) {
        value = '#' + value
      }

      if (/^#[0-9A-F]{6}$/i.test(value)) {
        setHsv(hexToHsv(value))
        handleColorChange(value)
        await saveColor()
      }
    },
    [handleColorChange, saveColor]
  )

  const handleHueMouseUp = useCallback(async () => {
    await saveColor()
  }, [saveColor])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging || !saturationRef.current) return
      const rect = saturationRef.current.getBoundingClientRect()
      const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
      const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height))
      const newS = (x / rect.width) * 100
      const newV = 100 - (y / rect.height) * 100
      const newHsv = {h: hsvRef.current.h, s: newS, v: newV}
      setHsv(newHsv)
      handleColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v))
    }

    const handleMouseUp = async () => {
      setIsDragging(false)
      await saveColor()
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleColorChange, saveColor])

  useEffect(() => {
    const handleClickOutside = async (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        await handleClosePicker()
      }
    }

    if (isPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isPickerOpen, handleClosePicker])

  const hasCustomColor = customColor !== undefined || optimisticColor !== null
  const localColor = customColor ?? optimisticColor ?? defaultColor
  const displayColor = isPickerOpen ? currentHexColor : localColor

  // If no color change handler is provided, render as read-only
  if (!onColorChange) {
    return <ColorPreview color={customColor || defaultColor} />
  }

  return (
    <div className="flex flex-col gap-2" data-cy={dataCy}>
      {error && (
        <div
          className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
          data-cy={`${dataCy}-error`}
        >
          {error}
        </div>
      )}
      <div className={`flex items-start gap-2 ${fullWidth ? 'w-full' : ''}`}>
        {/* Global Color Section - takes 50% width when fullWidth, removed from DOM when no global color shown */}
        {((showDefaultColorAsFallback && !hasCustomColor) || showDefaultColor) && (
          <div className={`flex flex-col ${fullWidth ? 'flex-1' : ''}`}>
            {showTitles && (
              <span className="text-xs text-gray-500 mb-1" data-cy={`${dataCy}-global-title`}>
                {t('localColors.globalColor')}
              </span>
            )}
            <div className="flex items-center">
              {/* Catalog mode: show global fallback when no local color */}
              {showDefaultColorAsFallback &&
                !hasCustomColor &&
                (showTitles ? (
                  <ColorButton
                    color={defaultColor}
                    isSelected={false}
                    isDisabled={isDisabled}
                    onClick={handleOpenPicker}
                    dataCy={`${dataCy}-global-fallback-btn`}
                  />
                ) : (
                  <Tooltip content={t('localColors.globalColor')}>
                    <ColorButton
                      color={defaultColor}
                      isSelected={false}
                      isDisabled={isDisabled}
                      onClick={handleOpenPicker}
                      dataCy={`${dataCy}-global-fallback-btn`}
                    />
                  </Tooltip>
                ))}

              {/* Installation modal mode: show global color (read-only) */}
              {showDefaultColor &&
                (showTitles ? (
                  <ColorButton
                    color={defaultColor}
                    isSelected={false}
                    isDisabled={true}
                    onClick={() => {}}
                    dataCy={`${dataCy}-global-btn`}
                  />
                ) : (
                  <Tooltip content={t('localColors.globalColor')}>
                    <ColorButton
                      color={defaultColor}
                      isSelected={false}
                      isDisabled={true}
                      onClick={() => {}}
                      dataCy={`${dataCy}-global-btn`}
                    />
                  </Tooltip>
                ))}
            </div>
          </div>
        )}

        {/* Local Color Button and Reset - takes 50% width when fullWidth, content starts from left */}
        <div className={`flex flex-col ${fullWidth ? 'flex-1' : ''}`}>
          {showTitles && (
            <span className="text-xs text-gray-500 mb-1" data-cy={`${dataCy}-local-title`}>
              {t('localColors.localColor')}
            </span>
          )}
          <div className="flex items-center gap-2">
            {/* Local Color Button (Editable) - shown when not using global fallback or when local color exists */}
            {(!showDefaultColorAsFallback || hasCustomColor) &&
              (showTitles ? (
                <ColorButton
                  color={displayColor}
                  isSelected={showSelectedOutline}
                  isDisabled={isDisabled}
                  onClick={handleOpenPicker}
                  dataCy={`${dataCy}-local-btn`}
                />
              ) : (
                <Tooltip
                  content={
                    hasCustomColor ? t('localColors.localColor') : t('localColors.setLocalColor')
                  }
                >
                  <ColorButton
                    color={displayColor}
                    isSelected={showSelectedOutline}
                    isDisabled={isDisabled}
                    onClick={handleOpenPicker}
                    dataCy={`${dataCy}-local-btn`}
                  />
                </Tooltip>
              ))}
            <div className="relative" ref={containerRef}>
              {isPickerOpen && (
                <div
                  ref={popoverRef}
                  className="absolute z-50 bg-white border border-border rounded-lg shadow-lg w-72"
                  style={{
                    marginTop: popoverPosition.top === '100%' ? '8px' : undefined,
                    marginBottom: popoverPosition.bottom === '100%' ? '8px' : undefined,
                    ...popoverPosition
                  }}
                  data-cy={`${dataCy}-picker-popover`}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <TextSemiBold>{t('localColors.customColor')}</TextSemiBold>
                    <button
                      type="button"
                      onClick={handleClosePicker}
                      className="p-1 rounded hover:bg-background-hover transition-colors"
                      data-cy={`${dataCy}-picker-close`}
                    >
                      <Icon path={mdiClose} customSize={1} />
                    </button>
                  </div>
                  <div className="p-4 flex flex-col gap-3">
                    <div
                      ref={saturationRef}
                      className="relative w-full h-20 rounded cursor-crosshair"
                      style={{
                        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))`
                      }}
                      onMouseDown={handleSaturationMouseDown}
                      data-cy={`${dataCy}-saturation-picker`}
                    >
                      <div
                        className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                        style={{
                          left: `${hsv.s}%`,
                          top: `${100 - hsv.v}%`,
                          backgroundColor: currentHexColor
                        }}
                        data-cy={`${dataCy}-saturation-indicator`}
                      />
                    </div>

                    {/* Hue slider */}
                    <div className="relative">
                      <style>
                        {`
                      .hue-slider::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        appearance: none;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 3px solid black;
                        cursor: pointer;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                      }
                      .hue-slider::-moz-range-thumb {
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 3px solid black;
                        cursor: pointer;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                      }
                    `}
                      </style>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={hsv.h}
                        onChange={handleHueChange}
                        onMouseUp={handleHueMouseUp}
                        onTouchEnd={handleHueMouseUp}
                        className="hue-slider w-full h-6 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background:
                            'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
                        }}
                        data-cy={`${dataCy}-hue-slider`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
                    <select
                      value={colorFormat}
                      disabled
                      className="px-2 py-1.5 border border-border rounded bg-background text-sm"
                      data-cy={`${dataCy}-format-select`}
                    >
                      <option value="HEX">HEX</option>
                    </select>
                    <input
                      type="text"
                      value={currentHexColor}
                      onChange={handleHexInputChange}
                      placeholder="#000000"
                      maxLength={7}
                      className="flex-1 px-2 py-1.5 border border-border rounded bg-background text-sm font-mono uppercase"
                      data-cy={`${dataCy}-hex-input`}
                    />
                  </div>
                </div>
              )}
            </div>

            {showResetButton && onColorReset && (
              <ResetLocalColorCell
                globalColor={defaultColor}
                localColor={customColor}
                onColorReset={onColorReset}
                isDisabled={isDisabled}
                dataCy={dataCy}
              />
            )}
          </div>
        </div>
      </div>

      {showResetButton && (
        <span className="text-[10px] text-gray-400">{t('localColors.appliedLocallyOnly')}</span>
      )}
    </div>
  )
}
